import { useCallback, useEffect, useRef, useState } from 'react';
import { socketService } from '@/services/SocketService';
import { useAudioCapture } from '@/hooks/useAudioCapture';

export type NoteRecorderStatus = 'idle' | 'recording' | 'transcribing' | 'done' | 'error';

function int16ToBase64(int16: Int16Array): string {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

interface NoteFinal {
  transcript: string;
  audioUrl: string;
}

/** Normalize for stop-word matching: lowercase, punctuation → spaces, collapse whitespace. */
function normalizeForMatch(s: string): string {
  return (s || '').toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
}

/** If `text` ENDS WITH `stopWord` (normalized, case-insensitive), return the text with that trailing
 *  phrase removed + matched=true; otherwise the text unchanged + matched=false. Trailing-only so it
 *  won't fire mid-sentence (pair with a distinctive phrase like "stop recording"). */
function stripTrailingStopWord(text: string, stopWord: string): { text: string; matched: boolean } {
  const nWord = normalizeForMatch(stopWord);
  if (!nWord) return { text, matched: false };
  const nText = normalizeForMatch(text);
  if (nText !== nWord && !nText.endsWith(' ' + nWord)) return { text, matched: false };
  const drop = nWord.split(' ').length;
  const words = text.trim().split(/\s+/);
  const kept = words.slice(0, Math.max(0, words.length - drop)).join(' ');
  return { text: kept.replace(/[\s,.;:!?-]+$/u, '').trim(), matched: true };
}

/**
 * Note Taker recorder (VIN-FEAT-03). Streams mic audio to the realtime-gateway with
 * `purpose: "note"`, surfaces interim captions from Google streaming STT, and resolves
 * with the finalized transcript + S3 audio URL. Reuses the existing socket audio transport
 * (`agent.audio_chunk` / `agent.audio_end` / `agent.audio_cancel`).
 */
export function useNoteRecorder(opts: { onFinal: (result: NoteFinal) => void; stopWord?: string }) {
  const [status, setStatus] = useState<NoteRecorderStatus>('idle');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);

  const noteIdRef = useRef<string>('');
  const audioUrlRef = useRef<string>('');
  const onFinalRef = useRef(opts.onFinal);
  onFinalRef.current = opts.onFinal;
  // Spoken stop word (chat_settings.note_stop_word) — empty/undefined disables voice-stop. Read via
  // a ref so the lifetime socket listener always sees the latest value without re-subscribing.
  const stopWordRef = useRef<string | undefined>(opts.stopWord);
  stopWordRef.current = opts.stopWord;
  // Lets the socket listener call stop() (defined below) without a stale closure / TDZ.
  const stopRef = useRef<() => void>(() => undefined);

  // Live mic level (0..1) for the recording visualizer — read via rAF, so a ref (no re-render).
  const levelRef = useRef(0);

  const onChunk = useCallback((int16: Int16Array) => {
    // Peak amplitude → normalized level for the waveform.
    let peak = 0;
    for (let i = 0; i < int16.length; i++) {
      const v = int16[i] < 0 ? -int16[i] : int16[i];
      if (v > peak) peak = v;
    }
    levelRef.current = Math.min(1, (peak / 32768) * 1.6);

    const noteId = noteIdRef.current;
    if (!noteId) return;
    socketService.emit('message', {
      type: 'agent.audio_chunk',
      purpose: 'note',
      note_id: noteId,
      media_reference_id: noteId,
      voice_input_mode: 'streaming',
      audio: int16ToBase64(int16),
    });
  }, []);

  const capture = useAudioCapture(onChunk);
  const captureRef = useRef(capture);
  captureRef.current = capture;

  // ── Socket listeners ──────────────────────────────────────────────────────
  // Ensures onFinal fires exactly once per recording (guards against duplicate final events).
  const resolvedRef = useRef(false);

  // Attach the note socket listeners for the HOOK'S LIFETIME (declarative), not imperatively in
  // start(). Attaching in start() meant a remount between start and the final event — which happens
  // on the agent-started path (navigate → mount → autostart, plus React StrictMode's
  // mount→cleanup→remount in dev) — tore the listeners down and never re-attached them, so the
  // client silently dropped every note:transcript (no interim shown, onFinal never fired → the panel
  // hung on "Transcribing"). Handlers gate on noteIdRef/resolvedRef so they always target the active
  // recording; the empty-dep effect re-attaches on every (re)mount.
  useEffect(() => {
    const onTranscript = (payload: unknown) => {
      const p = payload as { text?: string; final?: boolean; note_id?: string };
      if (!noteIdRef.current) {
        console.warn('[useNoteRecorder] note:transcript dropped — no active recording | evt_note_id=%s', p.note_id);
        return;
      }
      if (p.note_id && p.note_id !== noteIdRef.current) {
        console.warn('[useNoteRecorder] note:transcript dropped — note_id mismatch | evt=%s active=%s', p.note_id, noteIdRef.current);
        return;
      }
      if (p.final) {
        if (resolvedRef.current) {
          console.warn('[useNoteRecorder] final transcript ignored — already resolved | note_id=%s', noteIdRef.current);
          return;
        }
        resolvedRef.current = true;
        setInterim('');
        // Strip the trailing spoken stop word from the saved transcript (if it landed in the final).
        let finalText = p.text || '';
        if (stopWordRef.current) finalText = stripTrailingStopWord(finalText, stopWordRef.current).text;
        console.info('[useNoteRecorder] FINAL transcript → onFinal | note_id=%s chars=%d', noteIdRef.current, finalText.length);
        // Final transcript may arrive before or after note:audio; resolve when we have it.
        onFinalRef.current({ transcript: finalText, audioUrl: audioUrlRef.current });
        setStatus('done');
      } else {
        // Voice-stop: if enabled and the live transcript now ENDS WITH the stop word, end the
        // recording (owns the mic → immediate, no round-trip) and hide the stop word from the caption.
        const sw = stopWordRef.current;
        if (sw) {
          const { text: cleaned, matched } = stripTrailingStopWord(p.text || '', sw);
          if (matched) {
            setInterim(cleaned);
            console.info('[useNoteRecorder] stop word heard → ending recording | note_id=%s', noteIdRef.current);
            stopRef.current();
            return;
          }
        }
        setInterim(p.text || '');
      }
    };
    const onAudio = (payload: unknown) => {
      const p = payload as { audio_url?: string; note_id?: string };
      if (p.note_id && p.note_id !== noteIdRef.current) return;
      audioUrlRef.current = p.audio_url || '';
    };
    const onErr = (payload: unknown) => {
      const p = payload as { message?: string };
      if (!noteIdRef.current) return;
      setError(p.message || 'Recording failed.');
      setStatus('error');
    };
    socketService.on('note:transcript', onTranscript);
    socketService.on('note:audio', onAudio);
    socketService.on('note:error', onErr);
    return () => {
      socketService.off('note:transcript', onTranscript);
      socketService.off('note:audio', onAudio);
      socketService.off('note:error', onErr);
    };
  }, []);

  const start = useCallback(async (noteId: string) => {
    setError(null);
    setInterim('');
    audioUrlRef.current = '';
    resolvedRef.current = false;
    noteIdRef.current = noteId;
    console.info('[useNoteRecorder] start | note_id=%s', noteId);
    await captureRef.current.start();
    setStatus('recording');
    console.info('[useNoteRecorder] capturing (mic open) | note_id=%s', noteId);
  }, []);

  const stop = useCallback(() => {
    captureRef.current.stop();
    if (!noteIdRef.current) return;
    setStatus('transcribing');
    console.info('[useNoteRecorder] stop → audio_end | note_id=%s', noteIdRef.current);
    socketService.emit('message', {
      type: 'agent.audio_end',
      purpose: 'note',
      note_id: noteIdRef.current,
      media_reference_id: noteIdRef.current,
      voice_input_mode: 'streaming',
      stt_provider: 'google',
    });
  }, []);
  stopRef.current = stop;

  const cancel = useCallback(() => {
    captureRef.current.stop();
    if (noteIdRef.current) {
      console.info('[useNoteRecorder] cancel | note_id=%s', noteIdRef.current);
      socketService.emit('message', {
        type: 'agent.audio_cancel',
        purpose: 'note',
        note_id: noteIdRef.current,
        media_reference_id: noteIdRef.current,
      });
    }
    noteIdRef.current = '';
    setInterim('');
    setStatus('idle');
  }, []);

  // Cleanup on unmount — stop the mic (socket listeners are detached by the effect above).
  useEffect(() => {
    return () => {
      captureRef.current.stop();
    };
  }, []);

  return { status, interim, error, start, stop, cancel, captureError: capture.error, levelRef };
}
